import React, { useState, useEffect } from "react"
import dayjs, { Dayjs } from "dayjs"
import { Modal, Button, Tooltip, Typography, Space, message, DatePicker, Input, Radio, Divider, Checkbox, InputNumber } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faFileExport } from "@fortawesome/pro-regular-svg-icons"

import type { TMeetEvent } from "./ViewMeetLineUp"
import { gen, resolveEventNumber, swimminglyApi, saveFile, download } from "./utils"

interface MeetEventOrderTabProps {
  meetId: number
  meetName: string
  courseLabel?: string | null
  meetStartDateIso?: string | null
  meetEndDateIso?: string | null
  facilityNameDefault?: string | null
  eventOrderName: string | null
  eventOrderLoading: boolean
  meetEvents: TMeetEvent[]
  /** Pre-fill values from the meet's saved entry limits (null = no limit saved). */
  savedIndividualLimit?: number | null
  savedRelayLimit?: number | null
  savedTotalLimit?: number | null
}

const MeetEventOrderTab: React.FC<MeetEventOrderTabProps> = ({
  meetId,
  meetName,
  courseLabel,
  meetStartDateIso,
  meetEndDateIso,
  facilityNameDefault,
  eventOrderName,
  eventOrderLoading,
  meetEvents,
  savedIndividualLimit = null,
  savedRelayLimit = null,
  savedTotalLimit = null,
}) => {
  const [exportModalVisible, setExportModalVisible] = useState(false)
  const [hyvModalVisible, setHyvModalVisible] = useState(false)
  const [exportingHyv, setExportingHyv] = useState(false)
  const [hyvAgeUpDate, setHyvAgeUpDate] = useState<Dayjs | null>(
    meetStartDateIso ? dayjs(meetStartDateIso) : null,
  )
  const [hyvEndDate, setHyvEndDate] = useState<Dayjs | null>(
    meetEndDateIso ? dayjs(meetEndDateIso) : null,
  )
  const [hyvFacilityName, setHyvFacilityName] = useState(
    facilityNameDefault || meetName || "",
  )
  const [hyvConversionCode, setHyvConversionCode] = useState<"CN" | "CY" | null>(
    null,
  )
  const [hyvFormat, setHyvFormat] = useState<"ev3" | "hyv" | "both">("ev3")

  // Entry limits override for EV3 export
  const [ev3LimitsEnabled, setEv3LimitsEnabled] = useState(false)
  const [ev3IndLimit, setEv3IndLimit] = useState<number | null>(null)
  const [ev3RelayLimit, setEv3RelayLimit] = useState<number | null>(null)
  const [ev3TotalLimit, setEv3TotalLimit] = useState<number | null>(null)

  // Initialise limit values from meet's saved limits each time the modal opens
  useEffect(() => {
    if (!hyvModalVisible) return
    setEv3LimitsEnabled(false)
    setEv3IndLimit(savedIndividualLimit)
    setEv3RelayLimit(savedRelayLimit)
    setEv3TotalLimit(savedTotalLimit)
  }, [hyvModalVisible]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmHyvExport = async () => {
    if (!meetId) {
      message.error("Missing meet information for export.")
      return
    }

    if (!hyvAgeUpDate) {
      message.error("Please select an age-up date.")
      return
    }

    if (!hyvEndDate) {
      message.error("Please select a meet end date.")
      return
    }

    const ageUpDateStr = hyvAgeUpDate.format("MM/DD/YYYY")
    const endDateStr = hyvEndDate.format("MM/DD/YYYY")

    const params = new URLSearchParams()
    params.set("ageUpDate", ageUpDateStr)
    params.set("endDate", endDateStr)
    if (hyvFacilityName && hyvFacilityName.trim()) {
      params.set("facilityName", hyvFacilityName.trim())
    }
    const courseLabelUpper = (courseLabel || "").toUpperCase()
    const isLCM = courseLabelUpper.includes("LCM") || courseLabelUpper === "L"
    if (isLCM && hyvConversionCode) {
      params.set("courseMeetCode", hyvConversionCode)
    }
    params.set("format", hyvFormat)

    // Append entry limit overrides when the user has enabled them (EV3/both only)
    const includesEv3 = hyvFormat === "ev3" || hyvFormat === "both"
    if (includesEv3 && ev3LimitsEnabled) {
      params.set("ev3IndividualLimit", String(ev3IndLimit ?? 0))
      params.set("ev3RelayLimit", String(ev3RelayLimit ?? 0))
      params.set("ev3TotalLimit", String(ev3TotalLimit ?? 0))
    }

    try {
      setExportingHyv(true)
      const file = await (swimminglyApi as any).get(
        gen(`/api/exportHytekMeetEvents/${meetId}?${params.toString()}`),
      )
      const safeMeetTitle = meetName || "MeetEvents"
      const dateStamp = meetStartDateIso
        ? (() => {
            const d = new Date(meetStartDateIso)
            if (isNaN(d.getTime())) return ""
            const day = String(d.getUTCDate()).padStart(2, "0")
            const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" }).replace(".", "")
            return `${day}${month}${d.getUTCFullYear()}`
          })()
        : ""
      const safeName = safeMeetTitle.replace(/[^A-Za-z0-9_-]+/g, "_")
      download(
        file,
        `Meet Events-${safeName}${dateStamp ? `-${dateStamp}` : ""}-001.zip`,
      )
      setHyvModalVisible(false)
      setExportModalVisible(false)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error exporting HYV meet events", err)
      message.error("There was a problem exporting the HYV Meet Events file.")
    } finally {
      setExportingHyv(false)
    }
  }

  const handleExportCsv = () => {
    if (!meetEvents || meetEvents.length === 0) {
      message.error("No event order is available to export.")
      return
    }

    const hasCsvDivision = meetEvents.some((e) => !!e.division)

    const header = [
      "Event #",
      ...(hasCsvDivision ? ["Division"] : []),
      "Age Group",
      "Gender",
      "Distance",
      "Stroke",
    ]

    const escapeCell = (val: unknown): string => {
      if (val === null || val === undefined) return ""
      const str = String(val)
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`
      }
      return str
    }

    const rows = meetEvents.map((evt) => {
      const ageGroupRaw = evt.ageGroup
      let ageGroupCell: string

      if (ageGroupRaw === null || ageGroupRaw === undefined) {
        ageGroupCell = ""
      } else {
        const str = String(ageGroupRaw)
        // If the age group looks like a numeric range (e.g. "7-8"),
        // wrap it in ="..." so Excel treats it as text instead of a date.
        if (/^\d+\s*-\s*\d+$/.test(str)) {
          ageGroupCell = `="${str.replace(/"/g, '""')}"`
        } else {
          ageGroupCell = escapeCell(str)
        }
      }

      return [
        escapeCell(resolveEventNumber(evt.displayNumber, evt.eventNumber)),
        ...(hasCsvDivision ? [escapeCell(evt.division)] : []),
        ageGroupCell,
        escapeCell(evt.gender),
        escapeCell(evt.distance),
        escapeCell(evt.stroke),
      ]
    })

    const csvLines = [header.join(","), ...rows.map((r) => r.join(","))]
    const csv = `${csvLines.join("\n")}\n`

    const safeMeetTitle = (meetName || "Meet")
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")

    saveFile(csv, `${safeMeetTitle || "Meet"}__EventOrder.csv`)
    setExportModalVisible(false)
  }

  const hasDivision = meetEvents.some((e) => !!e.division)
  const { Paragraph } = Typography

  return (
    <div className="shadowed">
      {eventOrderName && (
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <h4 style={{ marginBottom: 0 }}>Event Order: {eventOrderName}</h4>
          <Tooltip title="Export meet events">
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: "auto" }}
              icon={
                <FontAwesomeIcon
                  icon={faFileExport}
                  className="fa-fw"
                />
              }
              onClick={() => setExportModalVisible(true)}
            />
          </Tooltip>
        </div>
      )}
      {eventOrderLoading ? (
        <p style={{ fontSize: 12, margin: 0 }}>Loading event order…</p>
      ) : meetEvents && meetEvents.length > 0 ? (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 12,
          }}
        >
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Event #</th>
              {hasDivision && <th style={{ textAlign: "left", padding: "4px 6px" }}>Division</th>}
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Age Group</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Gender</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Distance</th>
              <th style={{ textAlign: "left", padding: "4px 6px" }}>Stroke</th>
            </tr>
          </thead>
          <tbody>
            {meetEvents.map((evt) => (
              <tr key={evt.swimEventId}>
                <td style={{ padding: "4px 6px" }}>
                  {resolveEventNumber(evt.displayNumber, evt.eventNumber)}
                </td>
                {hasDivision && (
                  <td style={{ padding: "4px 6px" }}>{evt.division || "—"}</td>
                )}
                <td style={{ padding: "4px 6px" }}>{evt.ageGroup}</td>
                <td style={{ padding: "4px 6px" }}>{evt.gender}</td>
                <td style={{ padding: "4px 6px" }}>{evt.distance}</td>
                <td style={{ padding: "4px 6px" }}>{evt.stroke}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ fontSize: 12, margin: 0 }}>
          No event order is configured for this meet.
        </p>
      )}

      <Modal
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        footer={null}
        title="Export Meet Events"
      >
        <Paragraph style={{ fontSize: 12, marginBottom: 12 }}>
          Choose how you&apos;d like to export this meet&apos;s event order.
        </Paragraph>
        <Space orientation="vertical" style={{ width: "100%" }} size={12}>
          <div>
            <Button
              type="primary"
              block
              icon={
                <FontAwesomeIcon
                  icon={faFileExport}
                  className="fa-fw"
                />
              }
              loading={exportingHyv}
              onClick={() => setHyvModalVisible(true)}
            >
              EV3/HYV Events File
            </Button>
            <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
              Download an EV3 or HYV events file compatible with Hy-Tek and other team
              management systems.
            </Paragraph>
          </div>
          <div>
            <Button block onClick={handleExportCsv}>
              CSV Events File
            </Button>
            <Paragraph type="secondary" style={{ fontSize: 11, marginTop: 4 }}>
              Download a CSV of the event order (event #, age group, gender,
              distance, stroke) for spreadsheets and quick review.
            </Paragraph>
          </div>
        </Space>
      </Modal>

      <Modal
        open={hyvModalVisible}
        title="Meet Events Setup"
        onCancel={() => setHyvModalVisible(false)}
        onOk={handleConfirmHyvExport}
        okText="Export Meet Events File"
        cancelText="Cancel"
        destroyOnHidden
        width={600}
      >
        <Divider style={{ margin: "8px 0" }}>Meet Details</Divider>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4 }}>Meet Name</div>
          <Input value={meetName} disabled />
        </div>
        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>Age-up Date</div>
            <DatePicker
              style={{ width: "100%" }}
              value={hyvAgeUpDate}
              onChange={(d) => setHyvAgeUpDate(d)}
              format="MM/DD/YYYY"
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>Start Date</div>
            <Input
              value={
                meetStartDateIso
                  ? dayjs(meetStartDateIso).format("MM/DD/YYYY")
                  : ""
              }
              disabled
            />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>End Date</div>
            <DatePicker
              style={{ width: "100%" }}
              value={hyvEndDate}
              onChange={(d) => setHyvEndDate(d)}
              format="MM/DD/YYYY"
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 12,
            marginBottom: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ marginBottom: 4 }}>Course</div>
            <Input value={courseLabel || ""} disabled />
          </div>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ marginBottom: 4 }}>Team / Facility</div>
            <Input
              value={hyvFacilityName}
              onChange={(e) => setHyvFacilityName(e.target.value)}
              placeholder="Enter team or facility name"
            />
          </div>
        </div>

        <Divider style={{ margin: "8px 0" }}>File Format</Divider>
        <div style={{ marginBottom: 12 }}>
          <Radio.Group
            value={hyvFormat}
            onChange={(e) => setHyvFormat(e.target.value as "ev3" | "hyv" | "both")}
          >
            <Radio value="ev3">EV3 only</Radio>
            <Radio value="hyv">HYV only</Radio>
            <Radio value="both">Both</Radio>
          </Radio.Group>
        </div>

        {(hyvFormat === "ev3" || hyvFormat === "both") && (
          <div style={{ marginBottom: 12 }}>
            <Checkbox
              checked={ev3LimitsEnabled}
              onChange={(e) => setEv3LimitsEnabled(e.target.checked)}
            >
              Set Entry Limits in the File
            </Checkbox>
            {hyvFormat === "both" && (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 2 }}>
                Entry limits are written to the EV3 file only — not included in HYV.
              </Typography.Text>
            )}
            {ev3LimitsEnabled && (
              <div style={{ marginTop: 10 }}>
                {(
                  [
                    { label: "Individual", value: ev3IndLimit, setter: setEv3IndLimit, savedValue: savedIndividualLimit },
                    { label: "Relay",      value: ev3RelayLimit, setter: setEv3RelayLimit, savedValue: savedRelayLimit },
                    { label: "Total",      value: ev3TotalLimit, setter: setEv3TotalLimit, savedValue: savedTotalLimit },
                  ] as const
                ).map(({ label, value, setter, savedValue }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}
                  >
                    <span style={{ width: 70 }}>{label}</span>
                    <InputNumber
                      min={1}
                      max={99}
                      value={value ?? undefined}
                      disabled={value === null}
                      onChange={(v) => setter(typeof v === "number" ? v : null)}
                      style={{ width: 70 }}
                    />
                    <Checkbox
                      checked={value === null}
                      onChange={(e) =>
                        setter(e.target.checked ? null : (savedValue ?? 1))
                      }
                    >
                      No Limit
                    </Checkbox>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {(() => {
          const courseLabelUpper = (courseLabel || "").toUpperCase()
          const isLCM =
            courseLabelUpper.includes("LCM") || courseLabelUpper === "L"
          if (!isLCM) return null
          return (
            <>
              <Divider style={{ margin: "8px 0" }}>LCM Conversion</Divider>
              <div style={{ marginBottom: 8 }}>
                <Radio.Group
                  value={hyvConversionCode}
                  onChange={(e) =>
                    setHyvConversionCode(e.target.value as "CN" | "CY")
                  }
                >
                  <Radio value="CY">
                    Allow SCM/Yards entry times to convert to LCM (CY)
                  </Radio>
                  <Radio value="CN">
                    Do not convert SCM/Yards entry times to LCM (CN)
                  </Radio>
                </Radio.Group>
              </div>
            </>
          )
        })()}


      </Modal>
    </div>
  )
}

export default MeetEventOrderTab
